import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Result, Skeleton } from 'antd';
import { PagePath } from '@monorepo/client-types';
import { useFeatureFlagContext } from '@monorepo/feature-flag/client';
import { Button } from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  Account,
  AccountStatus,
  AccountType,
  ContractType,
  DashboardLayouts,
} from '@monorepo/types';
import ContractModal from '../../components/contract-modal/contract-modal';
import { useStore } from '../../helpers/use-store';
import FiversDashboard from './5ers-dashboard/5ers-dashboard';
import FiversDashboardNew from './5ers-dashboard-new/5ers-dashboard-new';
import { PaymentRequiredAlert } from './dashboard-common/payment-required-alert/payment-required-alert';
import { PaymentRequiredModal } from './dashboard-common/payment-required-modal/payment-required-modal';
import { WaitingWithdrawBanner } from './dashboard-common/waiting-withdraw-banner/waiting-withdraw-banner';
import DashboardWrapper from './dashboard-wrapper';
import TtpDashboard from './ttp-dashboard/ttp-dashboard';
import TtpDashboardNew from './ttp-dashboard-new/ttp-dashboard-new';

import './dashboard-container.scss';

interface DashboardContainerContextValue {
  updateAccountStatus: (status: AccountStatus) => void;
}

const DashboardContainerContext = createContext<DashboardContainerContextValue>(
  {} as DashboardContainerContextValue,
);

export const useDashboardContainer = () =>
  useContext(DashboardContainerContext);

const DashboardContainer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'errors']);
  const { id } = useParams<{ id: string }>();
  const { loadingState, updateLoadingState } = useLoading(LoadingState.Loading);
  const [isShowPaymentRequiredModal, setIsShowPaymentRequiredModal] =
    useState(false);
  const featureFlag = useFeatureFlagContext();

  const {
    dataStore: {
      accountStore,
      programStore,
      applicationConfigStore,
      addOnStore,
    },
  } = useStore();

  const [account, setAccount] = useState<Account>();

  useEffect(() => {
    const init = async () => {
      if (!id) {
        return;
      }

      try {
        const accountResponse =
          await accountStore.getByTradingSystemAccount(id);

        if (accountResponse) {
          if (applicationConfigStore.applicationConfig.enableMigrationSync) {
            try {
              await accountStore.sync(accountResponse.tsAccountId as string);
            } catch (e) {
              console.error(`failed syncing account`, e);
            }
          }

          setAccount(accountResponse);
          addOnStore.getAddOnsForAccount(accountResponse.tsAccountId as string);
        }
      } catch (e) {
        console.error(`Failed fetching account: ${id}`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [id]);

  useEffect(() => {
    if (!account) {
      return;
    }

    featureFlag.updateAttributes({
      tradingSystem: account.tradingSystem,
    });

    return () => {
      featureFlag.updateAttributes({
        tradingSystem: undefined,
      });
    };
  }, [account]);

  if (!id) {
    return null;
  }

  if (loadingState === LoadingState.Loading) {
    return <Skeleton active={true} />;
  }

  if (!account) {
    return (
      <Result
        status="404"
        title="404"
        subTitle={t('errors:page_unavailable')}
        extra={
          <Button type="primary" onClick={() => navigate(PagePath.Root)}>
            {t('common:back_home')}
          </Button>
        }
      />
    );
  }
  const program = programStore.get(account.programId);
  const programLayout = program?.config?.dashboardLayout;
  const applicationLayout =
    applicationConfigStore.applicationConfig.dashboardLayout;
  const selectedDashboard =
    programLayout || applicationLayout || DashboardLayouts.TTPDashboard;

  const getDashboard = () => {
    const dashboardLayoutToComponent: Partial<
      Record<DashboardLayouts, ReactNode>
    > = {
      [DashboardLayouts.TTPDashboard]: <TtpDashboard account={account} />,
      [DashboardLayouts.TTPDashboardNew]: <TtpDashboardNew account={account} />,
      [DashboardLayouts.FiversDashboardNew]: (
        <FiversDashboardNew account={account} />
      ),
      [DashboardLayouts.FiversDashboard]: <FiversDashboard account={account} />,
    };

    return dashboardLayoutToComponent[selectedDashboard];
  };

  const isFundedAccount = account.type === AccountType.Funded;
  const isWithdrawalPending =
    account.status === AccountStatus.WaitingForWithdraw;

  const updateAccountStatus = (status: AccountStatus) => {
    setAccount({
      ...account,
      status,
    });
  };

  const isShowPaymentRequiredAlert =
    account.status === AccountStatus.Disabled &&
    Boolean(account.state?.target) &&
    program?.quickBuyProgramId;

  const paymentRequiredAmount =
    program?.quickBuyProgramId &&
    programStore.get(program.quickBuyProgramId)?.price;

  return (
    <DashboardContainerContext.Provider value={{ updateAccountStatus }}>
      <PaymentRequiredModal
        isOpen={isShowPaymentRequiredModal}
        onClose={() => setIsShowPaymentRequiredModal(false)}
        accountId={account._id}
        amount={paymentRequiredAmount || 0}
      />
      {isShowPaymentRequiredAlert && (
        <PaymentRequiredAlert
          amount={paymentRequiredAmount || 0}
          onCompleteClick={() => setIsShowPaymentRequiredModal(true)}
        />
      )}
      {isWithdrawalPending && <WaitingWithdrawBanner />}
      <DashboardWrapper
        account={account}
        dashboard={selectedDashboard}
        program={program}
      >
        {isFundedAccount && (
          <ContractModal contractType={ContractType.Funded} />
        )}
        {isFundedAccount && (
          <ContractModal contractType={ContractType.FundedRules} />
        )}
        <ContractModal contractType={ContractType.Nyse} />
        <div className="dashboard-container"> {getDashboard()}</div>
      </DashboardWrapper>
    </DashboardContainerContext.Provider>
  );
};

export default DashboardContainer;
